import React, { useState, Fragment } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { CheckoutHeader } from '../components/layout/CheckoutHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { CheckoutFooter } from '../components/layout/CheckoutFooter';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { useScreenInit } from '../useScreenInit.js';
import { CouponModal } from '../components/modals/CouponModal';
import { NowCheckoutOrderSummary } from '../components/checkout/NowCheckoutOrderSummary';
import { NowOrderItemsSummary } from '../components/checkout/NowOrderItemsSummary';
import { RewardsVouchersSection } from '../components/checkout/RewardsVouchersSection';
import { LocationModal } from '../components/modals/LocationModal';
import { LoyaltyPointsSection } from '../components/checkout/LoyaltyPointsSection';
import { WalletBalance } from '../components/checkout/WalletBalance';
import { PrescriptionUploadSidebar } from '../components/prescription/PrescriptionUploadSidebar';
import { PaymentProcessingBottomSheet } from '../components/checkout/PaymentProcessingBottomSheet';
import { NowCheckoutExperience } from '../components/now/NowCheckoutExperience';
import { useStore } from '../lib/store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { MapPinIcon, EditIcon, TruckIcon, TagIcon, PackageIcon, ZapIcon, SnowflakeIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, AlertCircleIcon, HomeIcon, ArrowLeftIcon, XIcon } from 'lucide-react';
export function CheckoutPage() {
  const navigate = useNavigate();
  const createNowOrder = useStore(state => state.createNowOrder);
  const [searchParams] = useSearchParams();
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('express-refrigerated');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(() => searchParams.get('from') === 'now' ? 'apple' : '');
  const [coldChainAcknowledged, setColdChainAcknowledged] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const screenInit = useScreenInit();
  const isNowMode = searchParams.get('mode') === 'now' || screenInit.mode === 'now';
  const isNowOrigin = searchParams.get('from') === 'now';
  const [currentMobileStep, setCurrentMobileStep] = useState<'shipping' | 'payment' | 'complete'>(screenInit.currentMobileStep || (isNowOrigin ? 'complete' : 'shipping'));
  const [deliveryAddress, setDeliveryAddress] = useState({
    type: 'Home',
    address: 'Q0851+3GR, Al Quds, Riyadh 13214, Saudi Arabia, Riyadh, SA',
    name: 'Ahmed',
    phone: '530151938'
  });
  const [appliedLoyaltyPoints, setAppliedLoyaltyPoints] = useState<{
    program: string;
    amount: number;
  } | null>(null);
  const [walletBalance] = useState(250.0);
  const [appliedWalletBalance, setAppliedWalletBalance] = useState(0);
  const [paymentProcessingOpen, setPaymentProcessingOpen] = useState(false);
  const [paymentMethodSheetOpen, setPaymentMethodSheetOpen] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [isMobileSummaryExpanded, setIsMobileSummaryExpanded] = useState(false);
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);
  const [showShippingBreakdown, setShowShippingBreakdown] = useState(false);
  const orderItems = [{
    id: 1,
    name: 'Dyson Corrale Hair Straightener Prussian Blue',
    price: 1999,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop',
    coldChain: true,
    nowEligible: true,
    temperatureRange: '2–8°C'
  }, {
    id: 2,
    name: 'FREE - Beamarry Argan Smooth Conditioner 380 ML',
    price: 54.12,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop',
    coldChain: false,
    nowEligible: false
  }];
  const containsColdChain = orderItems.some(item => item.coldChain);
  const availableShippingMethods = containsColdChain ? ['express-refrigerated', 'same-day', 'standard'] : ['express-120', 'same-day', 'standard', 'express'];
  const shippingMethods = [{
    id: 'same-day',
    name: 'Same Day Delivery',
    description: 'Get it today',
    price: 0,
    icon: <ZapIcon className="w-5 h-5" data-id="element-6587" data-mp="1cvk1t8umrmqk" />,
    badge: 'Get it today',
    details: 'AC Controlled Delivery / Not Chilled'
  }, {
    id: 'standard',
    name: 'Standard Delivery',
    description: '3-5 business days',
    price: 0,
    icon: <PackageIcon className="w-5 h-5" data-id="element-6588" data-mp="31cagxuu6pvht" />
  }, {
    id: 'express-120',
    name: 'Express Delivery - 120 Minutes',
    description: 'Same day delivery within 2 hours',
    price: 15,
    icon: <ZapIcon className="w-5 h-5" data-id="element-6589" data-mp="2sfjl9jjjoinr" />
  }, {
    id: 'express-refrigerated',
    name: 'Express Refrigerated Delivery',
    description: 'Temperature controlled delivery',
    price: 0,
    icon: <SnowflakeIcon className="w-5 h-5" data-id="element-6590" data-mp="3g3ax6m16atoi" />
  }, {
    id: 'express',
    name: 'Express Delivery',
    description: '1-2 business days',
    price: 25,
    icon: <TruckIcon className="w-5 h-5" data-id="element-6591" data-mp="3baq7nwpav2qs" />
  }];
  const paymentMethods = [{
    id: 'apple',
    name: 'Apple Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/apple-pay.svg?v=2.6.2'
  }, {
    id: 'google',
    name: 'Google Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/google-pay.svg?v=2.6.2'
  }, {
    id: 'mada',
    name: 'Mada',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/mada.svg?v=2.6.2'
  }, {
    id: 'visa',
    name: 'Visa',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/visa.svg?v=2.6.2'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/master-card.svg?v=2.6.2'
  }, {
    id: 'tamara',
    name: 'Tamara',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/tamara.png?v=2.6.2'
  }, {
    id: 'tabby',
    name: 'Tabby',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/tabby.svg?v=2.6.2'
  }, {
    id: 'stc',
    name: 'STC Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/stc-bank.png?v=2.6.2'
  }, {
    id: 'express',
    name: 'Express Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/express-pay.svg?v=2.6.2'
  }, {
    id: 'emkan',
    name: 'Emkan',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/emkan.png?v=2.6.2'
  }, {
    id: 'madfu',
    name: 'Madfu',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/madfu.png?v=2.6.2'
  }, {
    id: 'cash',
    name: 'Cash On Delivery',
    icon: '💵'
  }].filter(method => !isNowOrigin || method.id !== 'tabby');
  // Payment method colors mapping
  const paymentMethodColors = {
    apple: 'bg-black hover:bg-gray-900',
    google: 'bg-white text-black border border-gray-300 hover:bg-gray-50',
    mada: 'bg-[#00A8E1] hover:bg-[#0090c5]',
    visa: 'bg-[#1A1F71] hover:bg-[#12154e]',
    mastercard: 'bg-[#EB001B] hover:bg-[#cc0017]',
    tamara: 'bg-[#1a1a1a] hover:bg-black',
    tabby: 'bg-[#3EDDB4] text-black hover:bg-[#32b594]',
    stc: 'bg-[#4F008C] hover:bg-[#3a0066]',
    express: 'bg-[#1E2B55] hover:bg-[#162041]',
    emkan: 'bg-[#00A651] hover:bg-[#008c44]',
    madfu: 'bg-[#5B4FFF] hover:bg-[#4a40cc]',
    cash: 'bg-brand hover:bg-brand-dark'
  };
  const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discount = 451.86;
  const selectedShipping = shippingMethods.find(m => m.id === selectedDeliveryMethod);
  const shippingCost = selectedShipping?.price || 0;
  const voucherDiscount = couponCode === 'LAKUM20' ? 20 : couponCode === 'WELCOME10' ? subtotal * 0.1 : 0;
  const effectiveShippingCost = couponCode === 'FREESHIP' ? 0 : shippingCost;
  const tax = 76.25;
  const loyaltyDiscount = appliedLoyaltyPoints?.amount || 0;
  const walletDiscount = appliedWalletBalance;
  const totalDiscounts = discount + loyaltyDiscount + walletDiscount + voucherDiscount;
  const total = subtotal - totalDiscounts + effectiveShippingCost + tax;
  const installmentOptions = [{
    provider: 'Tamara',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Tamara_Logo.svg/2560px-Tamara_Logo.svg.png',
    installments: 12,
    amount: 133,
    color: 'bg-[#1a1a1a]'
  }, {
    provider: 'Tabby',
    logo: 'https://tabby.ai/assets/images/tabby-logo.svg',
    installments: 4,
    amount: 400,
    color: 'bg-[#3EDDB4]'
  }, {
    provider: 'MADA',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/64/Mada_Logo.svg/2560px-Mada_Logo.svg.png',
    installments: 4,
    amount: 400,
    color: 'bg-[#00A8E1]'
  }, {
    provider: 'Modlu',
    logo: 'https://modlu.com/assets/images/logo.svg',
    installments: 6,
    amount: 267,
    color: 'bg-[#5B4FFF]'
  }].filter(option => !isNowOrigin || option.provider !== 'Tabby');
  const applyCoupon = (code: string) => {
    const normalizedCode = code.trim().toUpperCase();
    const supportedCodes = ['LAKUM20', 'FREESHIP', 'WELCOME10'];
    if (!supportedCodes.includes(normalizedCode)) {
      toast.error('This voucher is not available for this order.');
      return false;
    }
    if (normalizedCode === 'WELCOME10' && subtotal < 3000) {
      toast.error('Add more items to unlock the 10% welcome reward.');
      return false;
    }
    setCouponCode(normalizedCode);
    toast.success(normalizedCode === 'FREESHIP' ? 'Free delivery reward applied.' : 'Voucher applied to your order.');
    return true;
  };
  const handleLocationSelect = (location: any) => {
    setDeliveryAddress(location);
    setSelectedLocation(location);
    setLocationModalOpen(false);
  };
  const handleShippingMethodChange = (methodId: string) => {
    setSelectedDeliveryMethod(methodId);
    setCurrentMobileStep('payment');
  };
  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethod(methodId);
    setCurrentMobileStep('complete');
    setPaymentMethodSheetOpen(false);
  };
  const handleCheckout = () => {
    if (containsColdChain && !coldChainAcknowledged) {
      toast.error('Please acknowledge the chilled-delivery handoff before payment.');
      return;
    }
    if (isNowOrigin || window.innerWidth < 1024) {
      setPaymentProcessingOpen(true);
      return;
    }
    if (containsColdChain) {
      createNowOrder();
      navigate('/track-order?mode=now');
      return;
    }
    console.log('Processing checkout...');
  };
  const handleLoyaltyPointsApplied = (program: string, amount: number) => {
    setAppliedLoyaltyPoints({
      program,
      amount
    });
  };
  const handleWalletBalanceApplied = (amount: number) => {
    setAppliedWalletBalance(amount);
  };
  const handleWalletBalanceRemoved = () => {
    setAppliedWalletBalance(0);
  };
  const getProgramName = (programId: string) => {
    const programs = {
      tamayouz: 'Tamayouz Points',
      qitaf: 'Qitaf Points',
      mokafaa: 'Mokafaa Points'
    };
    return programs[programId] || 'Loyalty Points';
  };
  const getMobileButtonContent = () => {
    if (currentMobileStep === 'shipping') {
      return {
        text: 'Select Delivery Method',
        disabled: false,
        bgColor: 'bg-[#1E2B55]',
        icon: null
      };
    } else if (currentMobileStep === 'payment') {
      return {
        text: 'Select Payment Method',
        disabled: false,
        bgColor: 'bg-[#1E2B55]',
        icon: null
      };
    } else {
      const paymentMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);
      return {
        text: containsColdChain && !coldChainAcknowledged ? 'Acknowledge chilled delivery' : `Pay with ${paymentMethod?.name || 'Selected Method'}`,
        disabled: containsColdChain && !coldChainAcknowledged,
        bgColor: paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]',
        icon: paymentMethod?.logo || null
      };
    }
  };
  const handleMobileCheckout = () => {
    if (currentMobileStep === 'shipping') {
      document.getElementById('shipping-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else if (currentMobileStep === 'payment') {
      document.getElementById('payment-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      handleCheckout();
    }
  };
  const mobileButton = getMobileButtonContent();
  const handlePrescriptionUpload = (file: File) => {
    console.log('Prescription uploaded:', file.name);
  };
  const handleConsultDoctor = () => {
    console.log('Consult doctor clicked');
  };
  const handlePaymentSuccess = () => {
    setPaymentProcessingOpen(false);
    if (isNowOrigin || containsColdChain) {
      createNowOrder();
      navigate('/track-order?mode=now');
      return;
    }
    navigate('/thank-you');
  };
  const handlePaymentCancel = () => {
    setPaymentProcessingOpen(false);
  };
  const handlePaymentFailure = (reason: string) => {
    setPaymentProcessingOpen(false);
    setPaymentError(reason);
    setTimeout(() => {
      document.getElementById('payment-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  if (isNowMode) return <NowCheckoutExperience data-id="element-6592" data-mp="37yglzykr4zce" />;
  return <HelmetProvider data-id="element-6593" data-mp="37xf00z276sfw">
      <SEO title="Checkout" description="Complete your order at Innova Pharmacy with secure payment options and fast delivery across Saudi Arabia." canonical="/en/checkout/" hreflangAr="/checkout/" hreflangEn="/en/checkout/" hreflangDefault="/checkout/" data-id="element-6594" data-mp="380t369ior5oe" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6595" data-mp="3bgxbm4mrfaqx">
        {/* Simplified Header for Checkout */}
        <div className="hidden md:block" data-id="element-6596" data-mp="3pgf4av36f85k">
          <CheckoutHeader data-id="element-6597" data-mp="3pfdqwjyou9ko" />
        </div>
        <MobileInternalHeader title="Checkout" showActions={false} data-id="element-6598" data-mp="3pec81tbfkise" />

        <main className="flex-grow pt-14 md:pt-16 pb-32 md:pb-0" data-id="element-6599" data-mp="3kh2go84hdx1x">
          <h1 className="sr-only" data-id="element-6600" data-mp="31dn1ix1agi7">Checkout</h1>

          <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-7xl" data-id="element-6601" data-mp="336s4ts00so3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 sm:mb-8" data-id="element-6602" data-mp="3u86uv5mc1dvp">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold" data-id="element-6603" data-mp="bzftwf3g83de">
                Checkout
              </h1>

              {/* Desktop Stepper - Better Design */}
              <div className="hidden lg:flex items-center gap-4 min-w-[400px]" data-id="element-6604" data-mp="qp0pdzpzmv5l">
                {[{
                key: 'shipping',
                label: 'Delivery',
                step: 1
              }, {
                key: 'payment',
                label: 'Payment',
                step: 2
              }, {
                key: 'complete',
                label: 'Review',
                step: 3
              }].map((s, idx) => {
                const order = {
                  shipping: 0,
                  payment: 1,
                  complete: 2
                } as const;
                const currentIdx = order[currentMobileStep];
                const isCompleted = idx < currentIdx;
                const isActive = idx === currentIdx;
                return <Fragment key={s.key} data-id="element-6605" data-mp="1vne2xo5u1x1y" data-mpi={idx}>
                      <div className="flex items-center gap-3" data-id="element-6606" data-mp="1vofpaosau5yz">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300
                          ${isCompleted ? 'bg-emerald-500 text-white' : isActive ? 'bg-[#1E2B55] text-white ring-4 ring-[#1E2B55]/10' : 'bg-gray-100 text-gray-400'}
                        `} data-id="element-6607" data-mp="1qwxr28hnujhp">
                          {isCompleted ? <CheckIcon className="w-4 h-4" data-id="element-6608" data-mp="29o8bc6svry0m" /> : s.step}
                        </div>
                        <span className={`text-sm font-semibold ${isActive ? 'text-[#1E2B55]' : isCompleted ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-6609" data-mp="20na8hru6hwjm">
                          {s.label}
                        </span>
                      </div>
                      {idx < 2 && <div className="h-0.5 flex-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-6610" data-mp="30w8hbtzqxn86">
                          <motion.div className="h-full bg-emerald-500" initial={{
                      width: 0
                    }} animate={{
                      width: isCompleted ? '100%' : '0%'
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-6611" data-mp="35ua0eex69mjn" />
                        </div>}
                    </Fragment>;
              })}
              </div>
            </div>

            {/* Payment Error Alert - Mobile */}
            {paymentError && <div className="lg:hidden mb-4 bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-6612" data-mp="1z7ocgb7gsp6f">
                <div className="flex items-start gap-3" data-id="element-6613" data-mp="238qcgu97h59g">
                  <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-6614" data-mp="1k9dgsphi3d8t" />
                  <div data-id="element-6615" data-mp="1faiomwxn8cc1">
                    <h3 className="font-bold text-red-900 mb-1" data-id="element-6616" data-mp="1fb23u5vwsmmd">
                      Payment Failed
                    </h3>
                    <p className="text-sm text-red-700" data-id="element-6617" data-mp="1t4t4spvkqy6h">{paymentError}</p>
                    <p className="text-xs text-red-600 mt-2" data-id="element-6618" data-mp="1p26kda18gfjb">
                      Please try another payment method or contact support
                    </p>
                  </div>
                </div>
                <button onClick={() => setPaymentError(null)} className="mt-3 text-sm font-medium text-red-600 hover:text-red-700" data-id="element-6619" data-mp="wek9dgpoe8mf">
                  Dismiss
                </button>
              </div>}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-6620" data-mp="2lqmdfg3jx716">
              {/* Main Content Area */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6" data-id="element-6621" data-mp="2gs17r15j5v5c">
                {/* Delivery Address Section */}
                {isNowOrigin ? <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white p-3" data-id="element-6622" data-mp="2tbogzp5hhxhc">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand" data-id="element-6623" data-mp="2fc6zzvyfjoij"><MapPinIcon className="h-4 w-4" data-id="element-6624" data-mp="2k9p1x2e153h1" /></span>
                    <div className="min-w-0 flex-1" data-id="element-6625" data-mp="2kcbfpjrikf7p"><p className="text-sm font-bold text-gray-900" data-id="element-6626" data-mp="37zt20zs0el3a">{deliveryAddress.type}</p><p className="truncate text-xs text-gray-600" data-id="element-6627" data-mp="3cz5z0dclh8jl">{deliveryAddress.address}</p></div>
                    <button type="button" onClick={() => setLocationModalOpen(true)} className="shrink-0 rounded-md p-2 text-brand transition-colors duration-150 hover:bg-brand-light" aria-label="Edit delivery address" data-id="element-6628" data-mp="2ya9hzqsk8l6u"><EditIcon className="h-4 w-4" data-id="element-6629" data-mp="3qyxbg9ngqgvh" /></button>
                  </div> : <div className="bg-white rounded-lg border border-gray-100 p-3 sm:p-4 md:p-6" data-id="element-6630" data-mp="3vpdnbpbmxufq">
                    <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6631" data-mp="1r5lxhdy1mbns">Your Delivery Address</h2>
                    <div className="flex items-start gap-3 sm:gap-4" data-id="element-6632" data-mp="1v6pa7rksnrks"><div className="flex-shrink-0" data-id="element-6633" data-mp="1v6fnqdyyk2ju"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-6634" data-mp="1v8s44mp2i4us"><MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1E2B55]" data-id="element-6635" data-mp="1hdpw9u0nu5fc" /></div></div><div className="flex-1 min-w-0" data-id="element-6636" data-mp="1mcstwteb99da"><div className="flex items-center justify-between mb-2" data-id="element-6637" data-mp="17hcvydam0tbm"><h3 className="font-bold text-sm sm:text-base" data-id="element-6638" data-mp="17ftdcg1he66o">{deliveryAddress.type}</h3><button onClick={() => setLocationModalOpen(true)} className="text-[#1E2B55] hover:text-[#162041] flex-shrink-0" data-id="element-6639" data-mp="1cgpqzlbmx6pa"><EditIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6640" data-mp="yhq3igzuyxup" /></button></div><p className="text-xs sm:text-sm text-gray-600 mb-1 break-words" data-id="element-6641" data-mp="13frj2ry2sf1f">{deliveryAddress.address}</p><p className="text-xs sm:text-sm text-gray-600" data-id="element-6642" data-mp="2supoyqqx6m9w">{deliveryAddress.name}</p><p className="text-xs sm:text-sm text-gray-600" data-id="element-6643" data-mp="2svh7p0zf4pir">{deliveryAddress.phone}</p></div></div>
                  </div>}
                {/* Loyalty Points Section - NEW */}
                <LoyaltyPointsSection onPointsApplied={handleLoyaltyPointsApplied} compact={isNowOrigin} data-id="element-6644" data-mp="37xz6mfabq5y1" />
                {/* Wallet Balance Section - NEW */}
                <WalletBalance availableBalance={walletBalance} onBalanceApplied={handleWalletBalanceApplied} onBalanceRemoved={handleWalletBalanceRemoved} data-id="element-6645" data-mp="3grvr7pnubabc" />
                {/* Delivery Method Section */}
                {!isNowOrigin && <div id="shipping-section" className="bg-white rounded-lg border border-gray-100 p-3 sm:p-4 md:p-6" data-id="element-6646" data-mp="3jivk9f6avnd">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6647" data-mp="3vl7bwx7r5f02">
                    Please Select The Delivery Method
                  </h2>
                  <div className="space-y-3" data-id="element-6648" data-mp="21a2eian9dyt6">
                    {shippingMethods.filter(method => availableShippingMethods.includes(method.id)).map(method => <label key={method.id} className={`flex items-center justify-between p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedDeliveryMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-6649" data-mp="97rc5rpbv3dm">
                        <div className="flex items-center flex-1" data-id="element-6650" data-mp="n6h4ku17cevn">
                          <input type="radio" name="shipping" value={method.id} checked={selectedDeliveryMethod === method.id} onChange={() => handleShippingMethodChange(method.id)} className="mr-3" data-id="element-6651" data-mp="s6bv2wh2f2g6" />
                          <div className="flex items-center gap-2 sm:gap-3 flex-1" data-id="element-6652" data-mp="1axcvzq1qx82d">
                            <div className={`p-2 rounded-lg ${selectedDeliveryMethod === method.id ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-6653" data-mp="2vfx89f3l1ldz">
                              {method.icon}
                            </div>
                            <div className="flex-1" data-id="element-6654" data-mp="2hgnxww225lhx">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-6655" data-mp="3f2v60fxklvz1">
                                <p className="font-bold text-xs sm:text-sm" data-id="element-6656" data-mp="3f4xs4ovlr4yh">
                                  {method.name}
                                </p>
                                {method.badge && <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-6657" data-mp="30ei5d5njfoy7">
                                    {method.badge}
                                  </span>}
                              </div>
                              <p className="text-xs text-gray-500" data-id="element-6658" data-mp="3t12x477xe32a">
                                {method.description}
                              </p>
                              {method.details && <p className="text-xs text-gray-400 mt-1" data-id="element-6659" data-mp="33mot7ge53vc6">
                                  {method.details}
                                </p>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-2 sm:ml-4 flex-shrink-0" data-id="element-6660" data-mp="82tfq3xitdx">
                          <p className="font-bold text-sm sm:text-base text-green-600" data-id="element-6661" data-mp="549scyxwr4md">
                            {method.price === 0 ? 'Free' : `SAR ${method.price.toFixed(2)}`}
                          </p>
                        </div>
                      </label>)}
                  </div>
                </div>}
                {containsColdChain && <section className="rounded-lg border border-[#b8e2df] bg-[#f2fbfa] p-4" aria-labelledby="cold-chain-handoff-heading" data-id="element-6662" data-mp="28lpvpqgb3g7l">
                    <div className="flex items-start gap-3" data-id="element-6663" data-mp="23m3i3fwkfxy9"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#16726c] text-white" data-id="element-6664" data-mp="23l26997q77fh"><SnowflakeIcon className="h-5 w-5" data-id="element-6665" data-mp="2hg49fol6uiol" /></span><div data-id="element-6666" data-mp="2dcg0b1gwrf18"><h2 id="cold-chain-handoff-heading" className="text-sm font-bold text-[#173c3a]" data-id="element-6667" data-mp="2ddr2iwupgeq8">Temperature-protected handoff</h2><p className="mt-1 text-xs leading-5 text-[#416b67]" data-id="element-6668" data-mp="1pnwsso5on6a7">Your chilled items are packed for 2–8°C delivery. Please ensure someone can receive the order when the driver arrives.</p></div></div>
                    <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-xs font-medium leading-5 text-gray-700" data-id="element-6669" data-mp="1ypd18qcxsybs"><input type="checkbox" checked={coldChainAcknowledged} onChange={event => setColdChainAcknowledged(event.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 text-[#16726c] focus:ring-[#16726c]" data-id="element-6670" data-mp="1tndx3e9dhxds" /><span data-id="element-6671" data-mp="355yidv2dzn3k">I understand that temperature-protected items should be received promptly on delivery.</span></label>
                  </section>}
                {/* Payment Method Section */}
                <div id="payment-section" className="bg-white rounded-lg border border-gray-100 p-3 sm:p-4 md:p-6" data-id="element-6672" data-mp="2br4k5wsyr49j">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6673" data-mp="2gpfsu47iv8q6">
                    {isNowOrigin ? 'Payment method' : 'Select Payment Method'}
                  </h2>
                  {isNowOrigin ? <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-3" data-id="element-6674" data-mp="1nz78c5pikc8f">
                      <div className="flex h-10 w-12 shrink-0 items-center justify-center rounded-md bg-gray-50" data-id="element-6675" data-mp="1sytk0app1kf4">
                        {paymentMethods.find(method => method.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(method => method.id === selectedPaymentMethod)?.logo} alt="" className="h-6 max-w-10 object-contain" data-id="element-6676" data-mp="15ask1q6kzcbb" /> : <span className="text-xl" data-id="element-6677" data-mp="xpgl1e0u3mi2">{paymentMethods.find(method => method.id === selectedPaymentMethod)?.icon}</span>}
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-6678" data-mp="nsbwx8nfhgym"><p className="text-sm font-bold text-gray-900" data-id="element-6679" data-mp="nrk1pyxqtl8v">{paymentMethods.find(method => method.id === selectedPaymentMethod)?.name}</p><p className="mt-0.5 text-xs text-gray-500" data-id="element-6680" data-mp="esossf3bdx1v">Secure payment</p></div>
                      <button type="button" onClick={() => setPaymentMethodSheetOpen(true)} className="shrink-0 text-sm font-bold text-brand transition-colors duration-150 hover:text-brand-dark" data-id="element-6681" data-mp="eui3c5h1t1f5">Change</button>
                    </div> : <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-6682" data-mp="53qgne9k1v0">
                      {paymentMethods.map(method => <button key={method.id} onClick={() => handlePaymentMethodSelect(method.id)} className={`border-2 rounded-lg p-4 flex sm:flex-col items-center sm:justify-center gap-3 sm:gap-0 hover:border-[#1E2B55] transition-colors min-h-[64px] sm:min-h-[100px] w-full ${selectedPaymentMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200'}`} data-id="element-6683" data-mp="3n8qoish8qpxa">
                          <div className="w-12 sm:w-auto flex justify-center sm:mb-2" data-id="element-6684" data-mp="1a2k244eyitji">{method.logo ? <img src={method.logo} alt={method.name} className="h-6 sm:h-8 object-contain max-w-full" data-id="element-6685" data-mp="w7q38sjjxf9z" /> : <span className="text-2xl sm:text-3xl" data-id="element-6686" data-mp="111lvrfh1q9xr">{method.icon}</span>}</div>
                          <span className="text-sm font-medium text-left sm:text-center leading-tight flex-1 sm:flex-none" data-id="element-6687" data-mp="mcfxputy2prg">{method.name}</span>
                          {selectedPaymentMethod === method.id && <div className="sm:hidden w-5 h-5 rounded-full bg-[#1E2B55] text-white flex items-center justify-center flex-shrink-0" data-id="element-6688" data-mp="da6pjbm5f8h8"><CheckIcon className="w-3 h-3" data-id="element-6689" data-mp="i889rzvb9dua" /></div>}
                        </button>)}
                    </div>}
                  {/* Desktop Checkout Button - Below Payment Methods */}
                  <div className="hidden lg:block mt-6" data-id="element-6690" data-mp="3pwdrrrlgy5yc">
                    <button onClick={() => {
                    if (currentMobileStep === 'shipping') {
                      document.getElementById('shipping-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else if (currentMobileStep === 'payment') {
                      document.getElementById('payment-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else {
                      handleCheckout();
                    }
                  }} disabled={currentMobileStep === 'complete' && (!selectedPaymentMethod || containsColdChain && !coldChainAcknowledged)} className={`w-full py-4 rounded-lg font-bold text-white transition-colors flex items-center justify-center gap-3 text-base ${currentMobileStep === 'complete' && (!selectedPaymentMethod || containsColdChain && !coldChainAcknowledged) ? 'bg-gray-300 cursor-not-allowed' : currentMobileStep === 'complete' && selectedPaymentMethod ? paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]' : 'bg-[#1E2B55] hover:bg-[#162041]'}`} data-id="element-6691" data-mp="7p7hgfls3u8m">
                      {currentMobileStep === 'shipping' && <>
                          <TruckIcon className="w-5 h-5" data-id="element-6692" data-mp="103rawci4axit" />
                          Select Delivery Method
                        </>}
                      {currentMobileStep === 'payment' && <>
                          <TagIcon className="w-5 h-5" data-id="element-6693" data-mp="r44anu14jxrc" />
                          Select Payment Method
                        </>}
                      {currentMobileStep === 'complete' && selectedPaymentMethod && <>
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-6 w-auto" data-id="element-6694" data-mp="2j2p80zbpbz9q" /> : <span className="text-2xl" data-id="element-6695" data-mp="259ptp0nnob3r">
                                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                              </span>}
                            Pay with{' '}
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                          </>}
                    </button>
                  </div>
                </div>

                <div className="lg:hidden" data-id="element-6696" data-mp="1o94uucakp633">
                  <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} compact={isNowOrigin} data-id="element-6697" data-mp="1o5z7b1hlec9h" />
                </div>
              </div>
              {/* Checkout Sidebar - Desktop */}
              <div className="lg:col-span-1" data-id="element-6698" data-mp="lgiumx9yc1zz">
                <div className="space-y-4 lg:sticky lg:top-24" data-id="element-6699" data-mp="ldd7voospc7f">
                  <div className="hidden lg:block" data-id="element-6700" data-mp="7hj7ju237v5x">
                    <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} compact={isNowOrigin} data-id="element-6701" data-mp="cert84mg2hbx" />
                  </div>
                  {/* Prescription Upload - NEW */}
                  <PrescriptionUploadSidebar onPrescriptionUpload={handlePrescriptionUpload} onConsultDoctor={handleConsultDoctor} compact={isNowOrigin} data-id="element-6702" data-mp="vc2q5iklsyo7" />
                  {/* Order Summary */}
                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6" data-id="element-6703" data-mp="3veuiaqokkm6y">
                    {isNowOrigin && <NowCheckoutOrderSummary items={orderItems} subtotal={subtotal} discount={totalDiscounts} shippingCost={effectiveShippingCost} tax={tax} total={total} data-id="element-6704" data-mp="3qdwyk9nwvk3c" />}
                    {/* Order Totals */}
                    {!isNowOrigin && <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6" data-id="element-6705" data-mp="hyvbp03m9w9x">
                      <div className="flex justify-between text-xs sm:text-sm items-center" data-id="element-6706" data-mp="i0zetftoyr95">
                        <span className="text-gray-600 flex items-center gap-1" data-id="element-6707" data-mp="dy31a3j0dlc2">
                          Subtotal 
                          <button onClick={() => setShowDiscountBreakdown(true)} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center" data-id="element-6708" data-mp="948eusc0qs9a">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-6709" data-mp="11nxfopiz7hyf" /></button>
                        </span>
                        <span className="font-medium" data-id="element-6710" data-mp="wvdsd0uhl8gf">
                          SAR {subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6711" data-mp="195vadnwi0ohl">
                        <span className="text-gray-600" data-id="element-6712" data-mp="1e5hmtv4i60pc">Discount{couponCode ? ` · ${couponCode}` : ''}</span>
                        <span className="font-medium text-green-600" data-id="element-6713" data-mp="1i6thif28hc4e">
                          -SAR {(discount + voucherDiscount).toFixed(2)}
                        </span>
                      </div>
                      {appliedLoyaltyPoints && <div className="flex justify-between text-xs sm:text-sm" data-id="element-6714" data-mp="1s3of8vqon8hm">
                          <span className="text-gray-600" data-id="element-6715" data-mp="1wx0syattpuvl">
                            {getProgramName(appliedLoyaltyPoints.program)}
                          </span>
                          <span className="font-medium text-green-600" data-id="element-6716" data-mp="25wxkck7ll7uc">
                            -SAR {appliedLoyaltyPoints.amount.toFixed(2)}
                          </span>
                        </div>}
                      {appliedWalletBalance > 0 && <div className="flex justify-between text-xs sm:text-sm" data-id="element-6717" data-mp="6rw54n0qhntg">
                          <span className="text-gray-600" data-id="element-6718" data-mp="6pt6jelimmd4">Wallet Balance</span>
                          <span className="font-medium text-green-600" data-id="element-6719" data-mp="kkvl0q4fhk1j">
                            -SAR {appliedWalletBalance.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between text-xs sm:text-sm items-center" data-id="element-6720" data-mp="ujb9smfzgh7v">
                        <span className="text-gray-600 flex items-center gap-1" data-id="element-6721" data-mp="pkhy8zk9b0ac">
                          Shipping & Handling
                          <button onClick={() => setShowShippingBreakdown(true)} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center" data-id="element-6722" data-mp="yjbpeaetiygi">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-6723" data-mp="35a1ok59zlfp6" /></button>
                        </span>
                        <span className="font-medium text-green-600" data-id="element-6724" data-mp="30c9m4hbpub6q">
                          {effectiveShippingCost === 0 ? 'Free' : `SAR ${effectiveShippingCost.toFixed(2)}`}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6725" data-mp="16zkogodilemz">
                        <span className="text-gray-600" data-id="element-6726" data-mp="1b0mi8ponwyhk">Tax</span>
                        <span className="font-medium" data-id="element-6727" data-mp="x2696s3buvw9">
                          SAR {tax.toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 sm:pt-3 flex justify-between items-center" data-id="element-6728" data-mp="122atkt7cwarr">
                        <span className="font-bold text-sm sm:text-base" data-id="element-6729" data-mp="o3kux94w231a">
                          Total (Include Vat)
                        </span>
                        <span className="text-lg sm:text-xl font-bold text-[#1E2B55]" data-id="element-6730" data-mp="2igap7rz65e97">
                          SAR {total.toFixed(2)}
                        </span>
                      </div>
                      {(appliedLoyaltyPoints || appliedWalletBalance > 0 || voucherDiscount > 0 || couponCode === 'FREESHIP') && <div className="pt-2 border-t border-gray-200" data-id="element-6731" data-mp="1596dpw7stt8a">
                          <p className="text-xs text-green-600 font-medium" data-id="element-6732" data-mp="10gmpm5bhjfp8">
                            {couponCode === 'FREESHIP' ? 'Free delivery reward applied to this order.' : `You saved SAR ${(loyaltyDiscount + walletDiscount + voucherDiscount).toFixed(2)} with rewards, points & wallet!`}
                          </p>
                        </div>}
                    </div>}
                    {/* Desktop Complete Purchase Button */}
                    <button onClick={() => {
                    if (currentMobileStep === 'shipping') {
                      document.getElementById('shipping-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else if (currentMobileStep === 'payment') {
                      document.getElementById('payment-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else {
                      handleCheckout();
                    }
                  }} disabled={currentMobileStep === 'complete' && (!selectedPaymentMethod || containsColdChain && !coldChainAcknowledged)} className={`hidden lg:flex w-full py-2.5 sm:py-3 rounded-lg font-bold text-white transition-colors mb-3 sm:mb-4 text-sm sm:text-base items-center justify-center gap-2 ${currentMobileStep === 'complete' && (!selectedPaymentMethod || containsColdChain && !coldChainAcknowledged) ? 'bg-gray-300 cursor-not-allowed' : currentMobileStep === 'complete' && selectedPaymentMethod ? paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]' : 'bg-[#1E2B55] hover:bg-[#162041]'}`} data-id="element-6733" data-mp="cljzlu0y3676">
                      {currentMobileStep === 'shipping' && <>
                          <TruckIcon className="w-5 h-5 flex-shrink-0" data-id="element-6734" data-mp="11ljucxmv62z" />
                          <span data-id="element-6735" data-mp="zsc0ubaafte">Select Delivery Method</span>
                        </>}
                      {currentMobileStep === 'payment' && <>
                          <TagIcon className="w-5 h-5 flex-shrink-0" data-id="element-6736" data-mp="a0gohkl14846" />
                          <span data-id="element-6737" data-mp="eul0ii95j6c4">Select Payment Method</span>
                        </>}
                      {currentMobileStep === 'complete' && selectedPaymentMethod && <>
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-6 w-auto flex-shrink-0" data-id="element-6738" data-mp="2e9wbsfgr2tct" /> : <span className="text-2xl flex-shrink-0" data-id="element-6739" data-mp="2rzga993at4gb">
                                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                              </span>}
                            <span data-id="element-6740" data-mp="3ayu29lhgw546">
                              Pay with{' '}
                              {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                            </span>
                          </>}
                    </button>
                    {/* Free Delivery Banner */}
                    {!isNowOrigin && <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 flex items-start gap-2" data-id="element-6741" data-mp="1488r3hbpzmse">
                      <TruckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-6742" data-mp="ucnr1oq7owla" />
                      <p className="text-xs sm:text-sm text-green-700 leading-tight" data-id="element-6743" data-mp="gdo3ylickpr8">
                        <strong data-id="element-6744" data-mp="lb7k5qylvclt">Congratulations!</strong> You have Unlocked Free
                        Delivery 🎉
                      </p>
                    </div>}

                    {/* Order Summary Section - Mobile Only (Moved to bottom) */}
                    {!isNowOrigin && <div className="lg:hidden border-t border-gray-200 pt-4 mt-4 mb-4" data-id="element-6745" data-mp="2fne3xe5kf1pq">
                      <h2 className="text-base font-bold mb-3" data-id="element-6746" data-mp="40aoeb8egn25">
                        Order Summary
                      </h2>
                      
                      {isNowOrigin ? <NowOrderItemsSummary items={orderItems} compact data-id="element-6747" data-mp="dszu0ph9r6pk" /> : <div className="space-y-4" data-id="element-6748" data-mp="91hr3vvoqw1y">
                        {/* Shipment 1 */}
                        {orderItems.slice(0, Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6749" data-mp="httjhnad0tkk">
                            <div className="bg-[#4ADE80] px-3 py-2 flex items-center justify-between text-white" data-id="element-6750" data-mp="wr2celfba5ee">
                              <span className="font-bold text-xs" data-id="element-6751" data-mp="rrg1iimyh18r">Shipment 1 of 2</span>
                              <div className="flex items-center gap-1.5" data-id="element-6752" data-mp="2yi5q5jki0036">
                                <span className="border border-white/40 rounded px-1.5 py-0.5 text-[9px] font-bold flex items-center gap-1" data-id="element-6753" data-mp="2ykjlltj3beb9">
                                  <TruckIcon className="w-2.5 h-2.5" data-id="element-6754" data-mp="1kmxfxyxn8bvl" /> Express
                                </span>
                                <span className="text-[10px] font-medium" data-id="element-6755" data-mp="1pkfq1snecx56">Same day delivery</span>
                              </div>
                            </div>
                            <div className="p-3 space-y-3" data-id="element-6756" data-mp="1ylwzis6au0kw">
                              {orderItems.slice(0, Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6757" data-mp="11xqysgchzsbw">
                                  <div className="flex-shrink-0 w-14 h-14 bg-gray-50 rounded-lg overflow-hidden" data-id="element-6758" data-mp="1fn2mpoqeba7d">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6759" data-mp="j5y66n1cc94p" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6760" data-mp="ao62xgu9j8ct">
                                    <h3 className="text-xs font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6761" data-mp="6msur03p6t2q">
                                      {item.name}
                                    </h3>
                                    <div className="flex items-center justify-between" data-id="element-6762" data-mp="3su0unmvum7rd">
                                      <span className="text-xs text-gray-600" data-id="element-6763" data-mp="3svvh55mrq7xk">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6764" data-mp="3j4hc6ic1sfx1">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}

                        {/* Shipment 2 */}
                        {orderItems.slice(Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6765" data-mp="2ljy7kdjsgbdj">
                            <div className="bg-[#1E2B55] px-3 py-2 flex items-center justify-between text-white" data-id="element-6766" data-mp="2qiiyfmd5hipb">
                              <span className="font-bold text-xs" data-id="element-6767" data-mp="3e83sp1er82dw">Shipment 2 of 2</span>
                              <div className="flex items-center gap-1.5" data-id="element-6768" data-mp="3j5vldxn83sj5">
                                <span className="border border-white/40 rounded px-1.5 py-0.5 text-[9px] font-bold flex items-center gap-1" data-id="element-6769" data-mp="34iafjvyyrf7j">
                                  <TruckIcon className="w-2.5 h-2.5" data-id="element-6770" data-mp="39eirz1ls0ub0" /> Standard
                                </span>
                                <span className="text-[10px] font-medium" data-id="element-6771" data-mp="5rig7amwyr61">2-3 business days</span>
                              </div>
                            </div>
                            <div className="p-3 space-y-3" data-id="element-6772" data-mp="1qgcsdv5co0d0">
                              {orderItems.slice(Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6773" data-mp="2088tlf2qg6lg">
                                  <div className="flex-shrink-0 w-14 h-14 bg-gray-50 rounded-lg overflow-hidden" data-id="element-6774" data-mp="17jlbfsda5xc4">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6775" data-mp="12ltc4dab38xw" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6776" data-mp="2s0zp1ncldr9n">
                                    <h3 className="text-xs font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6777" data-mp="2nwjkbpepspw0">
                                      {item.name}
                                    </h3>
                                    <div className="flex items-center justify-between" data-id="element-6778" data-mp="qairn1gdbpke">
                                      <span className="text-xs text-gray-600" data-id="element-6779" data-mp="v7spn5htp9ju">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6780" data-mp="141nj3g9l1bjn">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                    </div>}

                    {/* Installment Options */}
                    {!isNowOrigin && <div className="space-y-2" data-id="element-6781" data-mp="21un9kvtl3hip">
                      {installmentOptions.map((option, index) => <div key={index} className="border border-gray-200 rounded-lg p-2 sm:p-3 flex items-center gap-2 sm:gap-3" data-id="element-6782" data-mp="1x3ew9op4vc09" data-mpi={index}>
                          <div className={`${option.color} rounded-lg p-1.5 sm:p-2 flex items-center justify-center w-10 sm:w-12 h-6 sm:h-8 flex-shrink-0`} data-id="element-6783" data-mp="1o41od0gnqvhn">
                            <span className="text-white text-xs font-bold" data-id="element-6784" data-mp="2gt8vuoy3avf1">
                              {option.provider}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-6785" data-mp="26sopx8zml2qh">
                            <p className="text-xs sm:text-sm font-medium leading-tight" data-id="element-6786" data-mp="10do4hx8882y5">
                              {option.installments} Installments of SAR{' '}
                              {option.amount}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-6787" data-mp="3ptdxh7nsxezg">
                              Interest-Free
                            </p>
                          </div>
                          <button className="text-xs font-medium text-[#1E2B55] hover:underline whitespace-nowrap flex-shrink-0 hidden sm:block" data-id="element-6788" data-mp="2fs6if2s65b2">
                            Learn More
                          </button>
                        </div>)}
                      </div>}
                    {/* Order Items - Desktop Only */}
                    {!isNowOrigin && <div className="hidden lg:block mt-6 pt-6 border-t border-gray-200" data-id="element-6789" data-mp="ldusel017m4n">
                      <h3 className="text-base font-bold mb-4" data-id="element-6790" data-mp="hcsnbnjfofur">Order Items</h3>
                      
                      {isNowOrigin ? <NowOrderItemsSummary items={orderItems} data-id="element-6791" data-mp="2n4y3tchqx4q9" /> : <div className="space-y-4" data-id="element-6792" data-mp="2n5r9sb8vrq7c">
                        {/* Shipment 1 */}
                        {orderItems.slice(0, Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6793" data-mp="290h7yu1ir4da">
                            <div className="bg-[#4ADE80] px-4 py-2.5 flex items-center justify-between text-white" data-id="element-6794" data-mp="2d0rdy6pb9us5">
                              <span className="font-bold text-sm" data-id="element-6795" data-mp="1z81o1r6nw5pr">Shipment 1 of 2</span>
                              <div className="flex items-center gap-2" data-id="element-6796" data-mp="247e9c7hvx4q8">
                                <span className="border border-white/40 rounded px-2 py-0.5 text-xs font-bold flex items-center gap-1" data-id="element-6797" data-mp="1pb6u7llc2tht">
                                  <TruckIcon className="w-3.5 h-3.5" data-id="element-6798" data-mp="1u8osmi1q5qbk" /> Express
                                </span>
                                <span className="text-xs font-medium" data-id="element-6799" data-mp="1lad2eh4rsujn">Same day delivery</span>
                              </div>
                            </div>
                            <div className="p-4 space-y-3" data-id="element-6800" data-mp="3aq2syvpobf3m">
                              {orderItems.slice(0, Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6801" data-mp="3d9zf433ec4pk">
                                  <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden" data-id="element-6802" data-mp="396krord4om23">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6803" data-mp="34b5f02quww79" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6804" data-mp="3s0zny98220m8">
                                    <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6805" data-mp="3n6c9l36rmmld">
                                      {item.name}
                                    </h4>
                                    <div className="flex items-center justify-between" data-id="element-6806" data-mp="jjlol20ruun5">
                                      <span className="text-sm text-gray-600" data-id="element-6807" data-mp="er29u1qcltgr">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6808" data-mp="r1jivzadtz5s">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}

                        {/* Shipment 2 */}
                        {orderItems.slice(Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6809" data-mp="1jr0grcgru89e">
                            <div className="bg-[#1E2B55] px-4 py-2.5 flex items-center justify-between text-white" data-id="element-6810" data-mp="1oqd7i8arjzh9">
                              <span className="font-bold text-sm" data-id="element-6811" data-mp="1sqx337fouv1a">Shipment 2 of 2</span>
                              <div className="flex items-center gap-2" data-id="element-6812" data-mp="2qg1mplehhcgy">
                                <span className="border border-white/40 rounded px-2 py-0.5 text-xs font-bold flex items-center gap-1" data-id="element-6813" data-mp="2qe8gg77s8u9h">
                                  <TruckIcon className="w-3.5 h-3.5" data-id="element-6814" data-mp="2z94w9im0d56b" /> Standard
                                </span>
                                <span className="text-xs font-medium" data-id="element-6815" data-mp="2v8l12kkzwbmt">2-3 business days</span>
                              </div>
                            </div>
                            <div className="p-4 space-y-3" data-id="element-6816" data-mp="22ivyw3wju5jm">
                              {orderItems.slice(Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6817" data-mp="2gjow4we8jeqf">
                                  <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden" data-id="element-6818" data-mp="3rxai68ost07q">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6819" data-mp="3myz9i1a8ovr3" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6820" data-mp="3li3n6hmwzkht">
                                    <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6821" data-mp="3qgdhyr4q8j2w">
                                      {item.name}
                                    </h4>
                                    <div className="flex items-center justify-between" data-id="element-6822" data-mp="i2xxoogtapuz">
                                      <span className="text-sm text-gray-600" data-id="element-6823" data-mp="n1rcqlbqyox9">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6824" data-mp="d80onhjj8gi3">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                    </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <CheckoutFooter data-id="element-6825" data-mp="11m3t46oi8lp0" />
        {/* Mobile Complete Purchase Button - Sits above the mobile navigation */}
        <div className="lg:hidden fixed bottom-[60px] left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" data-id="element-6826" data-mp="wpde7mofrw56">
          {/* Redesigned Stepper - Above Button */}
          {!isNowOrigin && <div className="flex items-center gap-2 mb-4 px-2" data-id="element-6827" data-mp="hywc013z9278">
            {(isNowOrigin ? [{
            key: 'payment',
            label: 'Payment'
          }, {
            key: 'complete',
            label: 'Review'
          }] : [{
            key: 'shipping',
            label: 'Delivery'
          }, {
            key: 'payment',
            label: 'Payment'
          }, {
            key: 'complete',
            label: 'Review'
          }]).map((s, idx) => {
            const currentIdx = isNowOrigin ? 1 : currentMobileStep === 'shipping' ? 0 : currentMobileStep === 'payment' ? 1 : 2;
            const isCompleted = idx < currentIdx;
            const isActive = idx === currentIdx;
            return <div key={s.key} className="flex-1 flex flex-col gap-1.5" data-id="element-6828" data-mp="21sqrnbugwbnm" data-mpi={idx}>
                  <div className="flex items-center justify-between px-1" data-id="element-6829" data-mp="25wezlvn0h99e">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? 'text-[#1E2B55]' : isCompleted ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-6830" data-mp="2au6zbbtwdzo5">
                      {s.label}
                    </span>
                    {isCompleted && <CheckIcon className="w-3 h-3 text-emerald-600" data-id="element-6831" data-mp="1n430dhrnmo3h" />}
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden relative" data-id="element-6832" data-mp="gkfpr7opbd40">
                    {(isActive || isCompleted) && <motion.div layoutId="step-fill" className={`absolute inset-0 rounded-full ${isCompleted ? 'bg-emerald-500' : 'bg-[#1E2B55]'}`} initial={{
                  x: '-100%'
                }} animate={{
                  x: 0
                }} transition={{
                  duration: 0.5,
                  ease: 'easeOut'
                }} data-id="element-6833" data-mp="pj9slfsmkzq0" />}
                  </div>
                </div>;
          })}
            </div>}

          {currentMobileStep === 'complete' ? <div className="flex items-center gap-4" data-id="element-6834" data-mp="1daf97x6e3w1d">
              <div className="flex-1" data-id="element-6835" data-mp="1wu4d85huf3wr">
                <p className="text-xs text-gray-500 font-medium" data-id="element-6836" data-mp="1wv72k8zioyak">
                  Total Amount
                </p>
                <p className="text-lg font-bold text-[#1E2B55]" data-id="element-6837" data-mp="21r4kukuccepz">
                  SAR {total.toFixed(2)}
                </p>
              </div>
              <button onClick={handleCheckout} disabled={!selectedPaymentMethod || containsColdChain && !coldChainAcknowledged} className={`flex-1 py-3.5 rounded-lg font-bold text-white transition-colors text-base flex items-center justify-center gap-2 ${!selectedPaymentMethod || containsColdChain && !coldChainAcknowledged ? 'bg-gray-300 cursor-not-allowed' : paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]'}`} data-id="element-6838" data-mp="1ruje9v2oavk1">
                {selectedPaymentMethod && <>
                    {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-5 w-auto" data-id="element-6839" data-mp="ybhz68l66t7a" /> : <span className="text-xl" data-id="element-6840" data-mp="u9wrr2ren74h">
                        {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                      </span>}
                    Pay Now
                  </>}
                {!selectedPaymentMethod && 'Select Payment'}
              </button>
            </div> : <button onClick={() => {
          if (currentMobileStep === 'shipping') {
            document.getElementById('shipping-section')?.scrollIntoView({
              behavior: 'smooth'
            });
          } else if (currentMobileStep === 'payment') {
            document.getElementById('payment-section')?.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }} className="w-full py-3.5 rounded-lg font-bold text-white transition-colors text-base flex items-center justify-center gap-2 bg-[#1E2B55] hover:bg-[#162041]" data-id="element-6841" data-mp="20xbki1wigjx7">
              {currentMobileStep === 'shipping' && <>
                  <TruckIcon className="w-5 h-5" data-id="element-6842" data-mp="262xgdq0ae6fb" />
                  Select Delivery Method
                </>}
              {currentMobileStep === 'payment' && <>
                  <TagIcon className="w-5 h-5" data-id="element-6843" data-mp="3hi3wkqt958ty" />
                  Select Payment Method
                </>}
            </button>}
        </div>
        {/* Modals */}
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => applyCoupon(code)} data-id="element-6844" data-mp="3cldg42djcb80" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={handleLocationSelect} initialAddress={deliveryAddress} data-id="element-6845" data-mp="3ojh7xz5vz2ts" />
        <AnimatePresence data-id="element-6846" data-mp="2bzpg9v0sj9h">
          {paymentMethodSheetOpen && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setPaymentMethodSheetOpen(false)} className="fixed inset-0 z-[100] bg-black/50" data-id="element-6847" data-mp="30xj3ge6p3g23" />
              <motion.section initial={{
            y: '100%'
          }} animate={{
            y: 0
          }} exit={{
            y: '100%'
          }} transition={{
            type: 'spring',
            damping: 26,
            stiffness: 260
          }} className="fixed inset-x-0 bottom-0 z-[101] max-h-[82dvh] overflow-y-auto rounded-t-3xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] md:left-1/2 md:max-w-lg md:-translate-x-1/2 md:rounded-2xl" role="dialog" aria-modal="true" aria-labelledby="now-payment-sheet-title" data-id="element-6848" data-mp="3asktrdcpio69">
                <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-200 md:hidden" data-id="element-6849" data-mp="2alpeu6bexarp" />
                <div className="flex items-center justify-between gap-4" data-id="element-6850" data-mp="25mmbqreztemd"><h3 id="now-payment-sheet-title" className="text-lg font-bold text-gray-900" data-id="element-6851" data-mp="25lbb30gugmzh">Choose payment method</h3><button type="button" onClick={() => setPaymentMethodSheetOpen(false)} className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Close payment methods" data-id="element-6852" data-mp="20mzwydjinqbg"><XIcon className="h-5 w-5" data-id="element-6853" data-mp="1rufuf203t1lc" /></button></div>
                <div className="mt-5 space-y-2" data-id="element-6854" data-mp="1mvv53xme42bo">{paymentMethods.map(method => <button key={method.id} type="button" onClick={() => handlePaymentMethodSelect(method.id)} className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors duration-150 ${selectedPaymentMethod === method.id ? 'border-brand bg-brand-light' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-6855" data-mp="1mwebqi6lb4ae"><span className="flex h-10 w-12 shrink-0 items-center justify-center rounded-md bg-white" data-id="element-6856" data-mp="18ygbsht0naef">{method.logo ? <img src={method.logo} alt="" className="h-6 max-w-10 object-contain" data-id="element-6857" data-mp="1xp2orsa68b3m" /> : <span className="text-xl" data-id="element-6858" data-mp="22lktis5i49fy">{method.icon}</span>}</span><span className="flex-1 text-sm font-bold text-gray-900" data-id="element-6859" data-mp="1ouxraq0j81f4">{method.name}</span>{selectedPaymentMethod === method.id && <CheckIcon className="h-5 w-5 text-brand" data-id="element-6860" data-mp="1svhotup0p71s" />}</button>)}</div>
              </motion.section>
            </>}
        </AnimatePresence>
        {/* Payment Processing Bottom Sheet - Mobile Only */}
        <PaymentProcessingBottomSheet isOpen={paymentProcessingOpen} onClose={() => setPaymentProcessingOpen(false)} paymentMethod={paymentMethods.find(m => m.id === selectedPaymentMethod)?.name || 'Selected Method'} onSuccess={handlePaymentSuccess} onCancel={handlePaymentCancel} onFailure={handlePaymentFailure} forceSuccess={isNowOrigin} data-id="element-6861" data-mp="1jwe5cplxupmr" />

        {/* Discount Breakdown Bottom Sheet / Modal */}
        <AnimatePresence data-id="element-6862" data-mp="socnfa1w04ma">
          {showDiscountBreakdown && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowDiscountBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-6863" data-mp="onsnjypximwg" />
              <motion.div initial={{
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: '100%',
            opacity: 0
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-6864" data-mp="1cb0xityayk4t">
                <div className="flex items-center justify-between mb-5" data-id="element-6865" data-mp="3i6v7sd5vv2s4">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-6866" data-mp="1m376m43a14f5">Discount breakdown</h3>
                  <button onClick={() => setShowDiscountBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-6867" data-mp="1r21n2zdrugw3">
                    <XIcon className="w-5 h-5" data-id="element-6868" data-mp="1vys2dkhr58gg" />
                  </button>
                </div>
                
                <div className="space-y-3" data-id="element-6869" data-mp="1zzkdyke3jpfw">
                  <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-6870" data-mp="251jivyphiuey">
                    <span data-id="element-6871" data-mp="29zb98oahe8h1">Subtotal ({orderItems.length} item) Excluding VAT</span>
                    <span className="flex-shrink-0" data-id="element-6872" data-mp="2eqkwji8k4zpr">{(subtotal + discount).toFixed(2)} SAR</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-900 font-medium gap-4" data-id="element-6873" data-mp="2jq5upqfwl41f">
                    <span className="uppercase" data-id="element-6874" data-mp="2nrrgk1af0snr">ONLINE EXCLUSIVE _ Save 50%</span>
                    <span className="flex-shrink-0" data-id="element-6875" data-mp="2sqc87cblq40l">-{discount.toFixed(2)} SAR</span>
                  </div>
                  
                  <div className="border-t border-dashed border-gray-200 pt-3 mt-1" data-id="element-6876" data-mp="h38y4ox7ghke">
                    <div className="flex justify-between text-sm font-bold text-gray-900 mb-2 gap-4" data-id="element-6877" data-mp="c3e96qwzq21z">
                      <span data-id="element-6878" data-mp="81spalm1gnia">Total ({orderItems.length} item) Excluding VAT</span>
                      <span className="flex-shrink-0" data-id="element-6879" data-mp="337pgnap85ud">{(subtotal - tax).toFixed(2)} SAR</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-6880" data-mp="zu03lfe2nnti">
                      <span data-id="element-6881" data-mp="vs57cpb000ml">+item VAT (15%)</span>
                      <span className="flex-shrink-0" data-id="element-6882" data-mp="vsew69kb41qp">{tax.toFixed(2)} SAR</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-3 mt-1" data-id="element-6883" data-mp="m2brhc3tozcj">
                    <div className="flex justify-between font-bold text-gray-900 text-base gap-4" data-id="element-6884" data-mp="lzzbv5ljf12n">
                      <span data-id="element-6885" data-mp="1jjak2vln6cso">Total ({orderItems.length} item) Including VAT</span>
                      <span className="flex-shrink-0" data-id="element-6886" data-mp="1epg2pytie9wr">{subtotal.toFixed(2)} SAR</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {/* Shipping Breakdown Bottom Sheet / Modal */}
        <AnimatePresence data-id="element-6887" data-mp="d6l582bjfbsx">
          {showShippingBreakdown && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowShippingBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-6888" data-mp="i54rk3u1mplt" />
              <motion.div initial={{
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: '100%',
            opacity: 0
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-6889" data-mp="wtr45xsad39c">
                <div className="flex items-center justify-between mb-5" data-id="element-6890" data-mp="391ofd3i01m9m">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-6891" data-mp="3nyfd78c0ypx4">Shipping breakdown</h3>
                  <button onClick={() => setShowShippingBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-6892" data-mp="3j043cxlqcfew">
                    <XIcon className="w-5 h-5" data-id="element-6893" data-mp="2vbb3wr5wfpfj" />
                  </button>
                </div>
                
                <div className="space-y-6" data-id="element-6894" data-mp="2qdjd60gzq9cx">
                  {/* Shipment 1 */}
                  <div data-id="element-6895" data-mp="351e4pdj9i7of">
                    <div className="inline-flex items-center gap-1 bg-[#4ADE80] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-6896" data-mp="352pbznbx1xka">
                      <TruckIcon className="w-3 h-3" data-id="element-6897" data-mp="2cjs04i41w7cg" /> Express
                    </div>
                    <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-6898" data-mp="27lgqo8ho3yur">
                      <span data-id="element-6899" data-mp="171g4axb1cwdc">1x Express delivery item(s)</span>
                      <span className="font-bold flex-shrink-0" data-id="element-6900" data-mp="1730yumxcofb7">0 SAR</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2" data-id="element-6901" data-mp="129op1ixbyd2i">
                      <div className="h-full bg-[#84cc16] w-[30%] rounded-full" data-id="element-6902" data-mp="xc6and0jn5n4"></div>
                    </div>
                    <p className="text-xs text-gray-600" data-id="element-6903" data-mp="sapetvwx29de">
                      Add <span className="font-bold text-gray-900" data-id="element-6904" data-mp="1pz9801thhabs">SAR 150.00</span> more to get <span className="font-bold text-green-600" data-id="element-6905" data-mp="1l0xyxtb0j3um">FREE delivery</span>
                    </p>
                  </div>

                  <div className="border-t border-gray-100" data-id="element-6906" data-mp="1g1363k7og46x"></div>

                  {/* Shipment 2 */}
                  <div data-id="element-6907" data-mp="1b2rxfct4bzqa">
                    <div className="inline-flex items-center gap-1 bg-[#1E2B55] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-6908" data-mp="1b5e3f80d276e">
                      <TruckIcon className="w-3 h-3" data-id="element-6909" data-mp="3vt0r2xo6dtss" /> Standard
                    </div>
                    <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-6910" data-mp="2bbrkjdzcgyax">
                      <span data-id="element-6911" data-mp="2gabadph26u8k">1x Standard delivery item(s)</span>
                      <span className="font-bold flex-shrink-0" data-id="element-6912" data-mp="2gauh0a19dw7a">{effectiveShippingCost === 0 ? 'Free' : `${effectiveShippingCost} SAR`}</span>
                    </div>
                    <p className="text-xs text-gray-600" data-id="element-6913" data-mp="2q1fo6lv0091t">
                      Estimated delivery: 2-3 business days
                    </p>
                  </div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>
      <MobileFooterNav data-id="element-6914" data-mp="340f517c9rjnf" />
    </HelmetProvider>;
}